'use client';

import { ReactNode } from 'react';

import { motion } from 'framer-motion';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';

import { LandingIcons } from '../icons';
import { siteConfig } from '../marketing-config';
import Section from '../section';
import GlowingBadge from '../ui/glowing-badge';

type FAQProps = {
  id?: string;
  heading?: string;
  items: {
    question?: string;
    answer?: ReactNode;
  }[];
};
export default function FAQ({ id = 'faq', heading, items }: FAQProps) {
  return (
    <Section id={id} className="px-6 lg:mb-24 lg:mt-8 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="text-center"
      >
        <GlowingBadge className="mb-4">
          <LandingIcons.faq />
          <span>FAQs</span>
        </GlowingBadge>
        <h2 className="mt-1 text-[32px] font-semibold leading-10 text-[#e6e6e6] lg:mt-2 lg:text-5xl lg:leading-normal">
          {heading}
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="mx-auto mb-12 mt-10 md:max-w-[655px] lg:mt-14"
      >
        {items.map((item, index) => (
          <Accordion
            key={index}
            type="single"
            collapsible
            className="mb-4 overflow-hidden rounded-2xl bg-[#1E1E21]/40 transition-all duration-300"
            defaultValue="0"
          >
            <AccordionItem value={String(index)} className="group border-none">
              <div className="relative lg:pb-2">
                <div className="absolute inset-0 overflow-hidden rounded-2xl">
                  <div
                    className="h-full w-full bg-gradient-to-bl from-white/55 to-white/0 transition-opacity duration-300 group-data-[state=closed]:opacity-0 group-data-[state=open]:opacity-100"
                    style={{
                      boxShadow:
                        '0px 4px 14px 0px rgba(0, 0, 0, 0.25) inset, 0px 4px 24px 0px rgba(255, 255, 255, 0.00) inset, -10px 24px 24px -10px rgba(0, 0, 0, 0.55)',
                    }}
                  >
                    <div className="absolute inset-[1px] rounded-2xl bg-[#1E1E21]" />
                  </div>
                </div>

                <AccordionTrigger className="relative z-10 p-4 text-left !leading-[1.3] text-white hover:no-underline lg:px-6 lg:pb-4 lg:pt-6 lg:text-xl lg:leading-normal">
                  {item.question}
                </AccordionTrigger>

                <AccordionContent className="relative z-10 px-4 pb-4 text-sm leading-normal text-white/50 lg:px-6 lg:pb-4 lg:text-base lg:leading-normal">
                  {item.answer}
                </AccordionContent>
              </div>
            </AccordionItem>
          </Accordion>
        ))}
      </motion.div>
      <motion.h4
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="mb-12 text-center text-sm font-medium tracking-tight text-[#e6e6e6]"
      >
        Still have questions? Email us at{' '}
        <a href={`mailto:${siteConfig.links.email}`} className="underline">
          {siteConfig.links.email}
        </a>
      </motion.h4>
    </Section>
  );
}
